<template>
  <div
    class="boss-message-box"
    :class="{'boss-message-box-can-send':canSendMessage}"
  >
    <img
      src="@/assets/images/avatar-company.svg"
      class="boss-message-box-avatar"
    >
    <div class="boss-layout-block">
      <div class="boss-message-reservation-header-layout">
        <div
          class="boss-layout-block boss-message-when boss-message-when-reservation"
          data-test="reservation-date"
        >
          {{ reservationStatusLabel }}
        </div>
        <div
          v-if="canSendMessage"
          class="boss-layout-block boss-message-reservation-delete-button"
          data-test="button-delete"
          @click="doRemove()"
        >
          <span class="boss-icon-trash pega-input-button vertical-alien-middle" />メッセージを削除する
        </div>
      </div>
      <applicant-message-subject :subject="messageReservation.message.subject" />
      <div
        :class="{'is-open': isOpen}"
        data-test="accordion"
      >
        <div class="boss-mini-accordion-body-grid margin-bottom-0">
          <div class="boss-mini-accordion-body-grid-content">
            <applicant-message-body :body="messageReservation.message.body" />
            <applicant-message-history-interview-adjustment
              v-if="messageReservation.interviewAdjustment"
              :candidate-dates="messageReservation.interviewAdjustment.candidateDates"
              :place="messageReservation.interviewAdjustment.place"
              :duration="messageReservation.interviewAdjustment.duration"
              :content-names="contentNames"
              :remarks="messageReservation.interviewAdjustment.remarks"
            />
            <div
              v-if="messageReservation.files.length"
              class="boss-layout-block"
              data-test="file-content"
            >
              <div>
                添付ファイル
              </div>
              <template v-for="file in messageReservation.files">
                <div
                  v-if="isFileExpired(file)"
                  :key="file.id"
                  class="position-relative pega-break"
                  :data-test="`file-${file.id}`"
                >
                  <span>{{ file.name }} 閲覧期限終了</span>
                </div>
                <div
                  v-else
                  :key="`download-${file.id}`"
                >
                  <button
                    class="position-relative pega-break boss-message-reservation-download-button"
                    :data-test="`file-${file.id}`"
                    @click="download(file.id)"
                  >
                    <span>{{ file | formatFile }}</span>
                  </button>
                </div>
              </template>
            </div>
            <div
              v-if="formatSelection"
              data-test="selection"
            >
              ※送信後に{{ formatSelection }}に更新します。
            </div>
          </div>
        </div>
        <div
          v-if="useAccordion"
          class="boss-mini-accordion-head custom margin-top-1"
          data-test="accordion-title"
          @click="toggle"
        >
          {{ accordionTitle }}
        </div>
      </div>
    </div>
    <div
      v-if="canSendMessage"
      class="boss-message-last-button-block"
    >
      <button
        class="boss-button pega-button"
        type="button"
        data-test="edit-message"
        :disabled="isWithdraw"
        @click="$emit('setMessage')"
      >
        予約メッセージを編集する
      </button>
    </div>
  </div>
</template>

<script>
import { now, createDate } from '@/lib/dateUtils.js'
import { mapGetters } from 'vuex'
import { formatBytes } from '@/lib/formatBytes'
import { downloadUrl } from '@/lib/download'
import ApplicantMessageSubject from '@/components/atoms/ApplicantMessageSubject.vue'
import ApplicantMessageBody from '@/components/atoms/ApplicantMessageBody.vue'
import ApplicantMessageHistoryInterviewAdjustment from '@/components/molecules/ApplicantMessageHistoryInterviewAdjustment'

export default {
  filters: {
    formatFile (file) {
      const sizeLabel = `(${formatBytes(file.size)})`
      return `${file.name}${sizeLabel} 閲覧期限 ${createDate(file.expiredDate).format('YYYY-MM-DD')}`
    }
  },
  components: {
    ApplicantMessageSubject,
    ApplicantMessageBody,
    ApplicantMessageHistoryInterviewAdjustment
  },
  props: {
    messageReservation: {
      type: Object,
      required: true
    },
    entryId: {
      type: Number,
      required: true
    },
    reservationStatusLabel: {
      type: String,
      required: true
    },
    useAccordion: {
      type: Boolean,
      default: false
    },
    isWithdraw: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isOpen: !this.useAccordion
    }
  },
  computed: {
    ...mapGetters([
      'canSendMessage'
    ]),
    accordionTitle () {
      return this.isOpen ? '閉じる' : '全て表示する'
    },
    contentNames () {
      if (!this.messageReservation.interviewAdjustment) return []

      const contentNames = this.messageReservation.interviewAdjustment.contentList.map(content => content.name)
      const contentOther = this.messageReservation.interviewAdjustment.contentOther
      return [...contentNames, contentOther].filter(v => v)
    },
    formatSelection () {
      const selection = this.messageReservation.selection
      if (selection.isPhaseChange) {
        if (selection.isStatusChange) {
          return `「選考フェーズ： ${selection.phase.name} 」、「選考ステータス： ${selection.status.name} 」`
        } else {
          return `「選考フェーズ： ${selection.phase.name} 」`
        }
      } else if (selection.isStatusChange) {
        return `「選考ステータス： ${selection.status.name} 」`
      } else {
        return ''
      }
    }
  },
  methods: {
    doRemove () {
      this.$dialog.confirm('メッセージを削除しますか？\n（予約中の場合は予約が削除されます。）')
        // はいを押下した時
        .then(() => {
          // 削除処理追加
          this.remove()
        })
        // いいえを押下した時（catch句を消すとコンソールに警告が出るから消さないでね！！）
        .catch(() => {})
    },
    remove () {
      this.$emit('remove')
    },
    isFileExpired (file) {
      return now().isAfter(createDate(file.expiredDate), 'days')
    },
    download (fileId) {
      this.$http.secured.get(`/entry/view/entries/${this.entryId}/files/${fileId}/url`).then((response) => {
        downloadUrl(response.data)
      }).catch(() => {
        // catch句を消すとコンソールに警告が出るから消さないでね！！
      })
    },
    toggle () {
      if (this.isOpen) {
        this.$scrollTo('#message-reservation-canceled', 750, { offset: -35 })
      }
      this.isOpen = !this.isOpen
    }
  }
}
</script>
